import { useState, useRef } from 'react';
import { Search, Filter, X, ChevronLeft, ChevronRight, ChevronDown } from 'lucide-react';
import Input from '@components/common/Input';
import DatePicker from '@components/common/DatePicker';
import Select from '@components/common/Select';
import Button from '@components/common/Button';
import { useAccounts } from '@hooks/useAccounts';
import { useCategories } from '@hooks/useCategories';
import { useClickOutside } from '@hooks/useClickOutside';
import { clsx } from 'clsx';

const MONTHS = [
  'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
  'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro',
];

const CURRENT_YEAR = new Date().getFullYear();
const YEARS = Array.from({ length: 8 }, (_, i) => CURRENT_YEAR - 3 + i);

const PERIOD_OPTIONS = [
  { value: 'month', label: 'Mês' },
  { value: '3m', label: '3 meses' },
  { value: '6m', label: '6 meses' },
  { value: '12m', label: '12 meses' },
  { value: 'custom', label: 'Personalizado' },
];

const fmtBR = (s) => {
  if (!s) return '—';
  const [y, m, d] = s.split('-');
  return `${d}/${m}/${y}`;
};

const TransactionFilters = ({ filters, onChange, onClear, selectedMonth, onMonthChange, periodMode = 'month', onPeriodMode }) => {
  const { accounts } = useAccounts();
  const { categories } = useCategories();
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [showMonthPicker, setShowMonthPicker] = useState(false);
  const [showYearPicker, setShowYearPicker] = useState(false);

  const monthPickerRef = useClickOutside(() => setShowMonthPicker(false));
  const yearPickerRef = useClickOutside(() => setShowYearPicker(false));

  const handlePreviousMonth = () => {
    if (selectedMonth.month === 0) {
      onMonthChange({ year: selectedMonth.year - 1, month: 11 });
    } else {
      onMonthChange({ ...selectedMonth, month: selectedMonth.month - 1 });
    }
  };

  const handleNextMonth = () => {
    if (selectedMonth.month === 11) {
      onMonthChange({ year: selectedMonth.year + 1, month: 0 });
    } else {
      onMonthChange({ ...selectedMonth, month: selectedMonth.month + 1 });
    }
  };

  const handleCurrentMonth = () => {
    const now = new Date();
    onMonthChange({ year: now.getFullYear(), month: now.getMonth() });
  };

  const accountOptions = [
    { value: '', label: 'Todas as contas' },
    ...accounts.map(acc => ({
      value: acc.accountId,
      label: `${acc.icon} ${acc.name}`,
    })),
  ];

  const categoryOptions = [
    { value: '', label: 'Todas as categorias' },
    ...categories.map(cat => ({
      value: cat.categoryId,
      label: `${cat.icon} ${cat.name}`,
    })),
  ];

  const typeOptions = [
    { value: '', label: 'Todos os tipos' },
    { value: 'income', label: '💰 Receitas' },
    { value: 'expense', label: '💸 Despesas' },
  ];

  const statusOptions = [
    { value: '', label: 'Todos os status' },
    { value: 'completed', label: '✅ Realizadas' },
    { value: 'pending', label: '⏳ Previstas' },
  ];

  const hasActiveFilters = filters.accountId || filters.categoryId || filters.type || filters.status || filters.search;

  const now = new Date();
  const isCurrentMonth = selectedMonth.month === now.getMonth() && selectedMonth.year === now.getFullYear();

  return (
    <div className="space-y-4">
      {/* Seletor de Período */}
      <div className="flex flex-wrap items-center gap-1 bg-white dark:bg-gray-800 rounded-xl p-1.5 border border-gray-200 dark:border-gray-700">
        {PERIOD_OPTIONS.map((opt) => (
          <button
            key={opt.value}
            onClick={() => onPeriodMode?.(opt.value)}
            className={clsx(
              'px-3 py-1.5 rounded-lg text-sm font-medium transition-colors',
              periodMode === opt.value
                ? 'bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-400'
                : 'text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
            )}
          >
            {opt.label}
          </button>
        ))}
      </div>

      {periodMode === 'month' ? (
      <div className="flex items-center justify-between bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-200 dark:border-gray-700">
        <button
          onClick={handlePreviousMonth}
          className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
        >
          <ChevronLeft size={20} className="text-gray-600 dark:text-gray-400" />
        </button>

        <div className="flex items-center gap-4">
          {!isCurrentMonth && (
            <button
              onClick={handleCurrentMonth}
              className="text-sm text-primary-600 hover:text-primary-700 font-medium"
            >
              Hoje
            </button>
          )}

          <div className="flex items-center gap-2">
            {/* Seletor de Mês */}
            <div className="relative" ref={monthPickerRef}>
              <button
                onClick={() => { setShowMonthPicker(!showMonthPicker); setShowYearPicker(false); }}
                className="flex items-center gap-1 text-lg font-semibold text-gray-900 dark:text-white hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
              >
                {MONTHS[selectedMonth.month]}
                <ChevronDown size={16} className="text-gray-400" />
              </button>

              {showMonthPicker && (
                <div className="absolute top-full left-1/2 -translate-x-1/2 mt-2 bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 p-2 z-50 w-48">
                  <div className="grid grid-cols-3 gap-1">
                    {MONTHS.map((m, i) => (
                      <button
                        key={m}
                        onClick={() => {
                          onMonthChange({ ...selectedMonth, month: i });
                          setShowMonthPicker(false);
                        }}
                        className={clsx(
                          'py-1.5 px-2 rounded-lg text-sm font-medium transition-colors',
                          selectedMonth.month === i
                            ? 'bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-400'
                            : 'hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300'
                        )}
                      >
                        {m.slice(0, 3)}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Seletor de Ano */}
            <div className="relative" ref={yearPickerRef}>
              <button
                onClick={() => { setShowYearPicker(!showYearPicker); setShowMonthPicker(false); }}
                className="flex items-center gap-1 text-sm text-gray-500 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 transition-colors font-medium"
              >
                {selectedMonth.year}
                <ChevronDown size={14} className="text-gray-400" />
              </button>

              {showYearPicker && (
                <div className="absolute top-full left-1/2 -translate-x-1/2 mt-2 bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 p-2 z-50">
                  <div className="flex flex-col gap-1">
                    {YEARS.map(y => (
                      <button
                        key={y}
                        onClick={() => {
                          onMonthChange({ ...selectedMonth, year: y });
                          setShowYearPicker(false);
                        }}
                        className={clsx(
                          'px-6 py-1.5 rounded-lg text-sm font-medium transition-colors',
                          selectedMonth.year === y
                            ? 'bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-400'
                            : 'hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300'
                        )}
                      >
                        {y}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        <button
          onClick={handleNextMonth}
          className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
        >
          <ChevronRight size={20} className="text-gray-600 dark:text-gray-400" />
        </button>
      </div>
      ) : (
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-200 dark:border-gray-700">
          <span className="text-sm font-medium text-gray-500 dark:text-gray-400">
            {periodMode === 'custom' ? 'Período personalizado' : `Últimos ${periodMode.replace('m', '')} meses`}
          </span>
          {periodMode === 'custom' ? (
            <div className="flex items-center gap-2">
              <DatePicker
                value={filters.startDate || ''}
                onChange={(e) => onChange({ ...filters, startDate: e.target.value })}
              />
              <span className="text-gray-400">–</span>
              <DatePicker
                value={filters.endDate || ''}
                onChange={(e) => onChange({ ...filters, endDate: e.target.value })}
              />
            </div>
          ) : (
            <span className="text-base font-semibold text-gray-900 dark:text-white">
              {fmtBR(filters.startDate)} – {fmtBR(filters.endDate)}
            </span>
          )}
        </div>
      )}

      {/* Filtros Principais */}
      <div className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-200 dark:border-gray-700">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1">
            <Input
              placeholder="Buscar por descrição..."
              value={filters.search || ''}
              onChange={(e) => onChange({ ...filters, search: e.target.value })}
              leftIcon={<Search size={18} />}
            />
          </div>

          <Select
            options={typeOptions}
            value={filters.type || ''}
            onChange={(e) => onChange({ ...filters, type: e.target.value })}
            fullWidth={false}
            containerClassName="w-full md:w-40"
          />

          <Select
            options={statusOptions}
            value={filters.status || ''}
            onChange={(e) => onChange({ ...filters, status: e.target.value })}
            fullWidth={false}
            containerClassName="w-full md:w-40"
          />

          <Button
            variant={showAdvanced ? 'primary' : 'secondary'}
            leftIcon={<Filter size={18} />}
            onClick={() => setShowAdvanced(!showAdvanced)}
          >
            Filtros
          </Button>
        </div>

        {showAdvanced && (
          <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Select
                label="Conta"
                options={accountOptions}
                value={filters.accountId || ''}
                onChange={(e) => onChange({ ...filters, accountId: e.target.value })}
              />

              <Select
                label="Categoria"
                options={categoryOptions}
                value={filters.categoryId || ''}
                onChange={(e) => onChange({ ...filters, categoryId: e.target.value })}
              />
            </div>
          </div>
        )}

        {hasActiveFilters && (
          <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700 flex items-center justify-between">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-sm text-gray-500 dark:text-gray-400">Filtros ativos:</span>
              {filters.search && (
                <span className="inline-flex items-center gap-1 px-2 py-1 bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-400 rounded-full text-xs">
                  Busca: &quot;{filters.search}&quot;
                  <button onClick={() => onChange({ ...filters, search: '' })}>
                    <X size={12} />
                  </button>
                </span>
              )}
              {filters.type && (
                <span className="inline-flex items-center gap-1 px-2 py-1 bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-400 rounded-full text-xs">
                  {filters.type === 'income' ? 'Receitas' : 'Despesas'}
                  <button onClick={() => onChange({ ...filters, type: '' })}>
                    <X size={12} />
                  </button>
                </span>
              )}
              {filters.accountId && (
                <span className="inline-flex items-center gap-1 px-2 py-1 bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-400 rounded-full text-xs">
                  Conta
                  <button onClick={() => onChange({ ...filters, accountId: '' })}>
                    <X size={12} />
                  </button>
                </span>
              )}
              {filters.categoryId && (
                <span className="inline-flex items-center gap-1 px-2 py-1 bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-400 rounded-full text-xs">
                  Categoria
                  <button onClick={() => onChange({ ...filters, categoryId: '' })}>
                    <X size={12} />
                  </button>
                </span>
              )}
            </div>
            <Button variant="ghost" size="sm" onClick={onClear}>
              Limpar filtros
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default TransactionFilters;
